import { useTranslation } from 'react-i18next'
import { Building2, Check, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { DropdownMenuItem, DropdownMenuSeparator } from '@/shared/components/ui/dropdown-menu'
import type { AuthUser } from '@/shared/lib/auth/auth-provider'

interface TenantSwitcherProps {
  user: AuthUser
  switchTenant: (tenantId: number) => Promise<void>
  isSwitchingTenant: boolean
}

export function TenantSwitcher({ user, switchTenant, isSwitchingTenant }: TenantSwitcherProps) {
  const { t } = useTranslation()

  if (user.tenants.length === 0) return null

  const isMultiTenant = user.tenants.length > 1

  async function handleSwitch(tenantId: number) {
    if (tenantId === user.organizationId || isSwitchingTenant) return
    try {
      await switchTenant(tenantId)
    } catch {
      toast.error(t('tenant.switchFailed'))
    }
  }

  return (
    <>
      <div className="px-2 py-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
        {t('tenant.label')}
      </div>
      {user.tenants.map((tenant) => {
        const isCurrent = tenant.id === user.organizationId
        const isClickable = isMultiTenant && !isCurrent && !isSwitchingTenant

        return (
          <DropdownMenuItem
            key={tenant.id}
            className="gap-2 text-xs"
            disabled={!isClickable}
            onClick={isClickable ? () => void handleSwitch(tenant.id) : undefined}
          >
            <Building2 className="h-3.5 w-3.5" />
            <span className="flex-1 truncate">{tenant.name}</span>
            {isCurrent && <Check className="h-3.5 w-3.5 text-primary" />}
            {isSwitchingTenant && !isCurrent && (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            )}
          </DropdownMenuItem>
        )
      })}
      <DropdownMenuSeparator />
    </>
  )
}
